import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { wizardApi } from "@/lib/api";
import type { WizardApplyRequest, WizardPreview } from "@/types/wizard";

export const queryKeys = {
  wizardState: ["wizard", "state"] as const,
  bridgeStatus: ["bridge", "status"] as const,
  wizardHealth: ["wizard", "health"] as const,
  wizardResult: ["wizard", "result"] as const,
};

export function useWizardState() {
  return useQuery({
    queryKey: queryKeys.wizardState,
    queryFn: () => wizardApi.getState(),
    refetchInterval: (query) => {
      const phase = query.state.data?.phase;
      return phase === "pending" ? 8000 : false;
    },
    refetchOnWindowFocus: true,
    staleTime: 1500,
  });
}

export function useBridgeStatus() {
  return useQuery({
    queryKey: queryKeys.bridgeStatus,
    queryFn: () => wizardApi.getStatus(),
    refetchInterval: 15000,
    staleTime: 2500,
  });
}

export function useWizardHealth() {
  return useQuery({
    queryKey: queryKeys.wizardHealth,
    queryFn: () => wizardApi.getHealth(),
    refetchInterval: 10000,
    staleTime: 2000,
  });
}

export function useWizardPreview() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => wizardApi.preview(),
    onSuccess: (data) => {
      client.setQueryData<WizardPreview>(["wizard", "preview"], data);
    },
  });
}

export function useWizardApply() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WizardApplyRequest) => wizardApi.apply(body),
    onSuccess: (data) => {
      client.setQueryData<WizardPreview>(queryKeys.wizardResult, data);
      void client.invalidateQueries({ queryKey: queryKeys.wizardState });
      void client.invalidateQueries({ queryKey: queryKeys.bridgeStatus });
    },
  });
}

/**
 * Final "Finish" mutation. Calls POST /api/bridge/wizard/complete which writes AllowAnonymous=false and returns a
 * sign-in challenge URL for the SPA to open.
 */
export function useWizardResult(enabled = false) {
  return useQuery({
    queryKey: queryKeys.wizardResult,
    queryFn: () => wizardApi.result(),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useWizardComplete() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => wizardApi.complete(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.wizardState });
      void client.invalidateQueries({ queryKey: queryKeys.bridgeStatus });
    },
  });
}
